import {
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  SearchOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Divider,
  Drawer,
  Dropdown,
  Input,
  Layout,
  List,
  Menu,
  Modal,
  Space,
  Typography,
} from 'antd';
import type { InputRef } from 'antd/es/input';
import type { MenuProps } from 'antd';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { APP_LOGO_URL, APP_NAME, APP_TAGLINE } from '../../constants/app';
import { ANIMATIONS, LAYOUT, SHADOWS, SPACING, TYPOGRAPHY } from '../../constants/design';
import {
  GLOBAL_SAVE_FORM_EVENT,
  GLOBAL_SHORTCUTS,
  getShortcutDisplay,
  SHORTCUT_KEYS,
} from '../../constants/shortcuts';
import { useColors } from '../../contexts/ThemeContext';
import type { MenuItem } from '../../services/authService';
import { useAuthStore } from '../../stores/authStore';
import { useGlobalStore } from '../../stores/globalStore';
import { useTableSettingsStore } from '../../stores/tableSettingsStore';
import { getFontFamilyFromCustomization } from '../../utils/colorUtils';
import { getIconComponent } from '../../utils/iconMapper';
import { normalizePath } from '../../utils/pathUtils';
import CustomizationPanel from '../common/CustomizationPanel';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

type FlattenedMenuItem = { path: string; label: string; key: string; breadcrumb: string };

const MenuSearchModal: FC<{
  open: boolean;
  onClose: () => void;
  searchValue: string;
  onSearchChange: (v: string) => void;
  items: FlattenedMenuItem[];
  onSelect: (path: string) => void;
  colors: { text: { secondary: string } };
  menuSearchShortcutDisplay: string;
}> = ({ open, onClose, searchValue, onSearchChange, items, onSelect, colors, menuSearchShortcutDisplay }) => {
  const inputRef = useRef<InputRef>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (open) {
      onSearchChange('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, onSearchChange]);

  useEffect(() => {
    setSelectedIndex((i) => (items.length ? Math.min(i, items.length - 1) : 0));
  }, [items.length]);

  useEffect(() => {
    const el = itemRefs.current[selectedIndex];
    if (el && listRef.current) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedIndex]);

  useEffect(() => {
    if (!open || items.length === 0) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, items.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const item = items[selectedIndex];
        if (item) onSelect(item.path);
        return;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, items, selectedIndex, onSelect]);

  return (
    <Modal
      title="Search menu"
      open={open}
      onCancel={onClose}
      footer={
        <div style={{ textAlign: 'center', color: colors.text.secondary, fontSize: 12 }}>
          <span style={{ marginRight: 8 }}>↑↓ move</span>
          <span style={{ marginRight: 8 }}>Enter select</span>
          <span style={{ marginRight: 8 }}>Esc close</span>
          <span>·</span>
          <span style={{ marginLeft: 8 }}>{menuSearchShortcutDisplay} to open</span>
        </div>
      }
      width={480}
      destroyOnClose
      styles={{ body: { paddingTop: 0 } }}
    >
      <Input
        ref={inputRef}
        prefix={<SearchOutlined style={{ color: colors.text.secondary }} />}
        placeholder="Search menu..."
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
        allowClear
        style={{ marginBottom: 16 }}
      />
      <div ref={listRef} style={{ maxHeight: 400, overflowY: 'auto' }}>
        <List
          dataSource={items}
          renderItem={(m, index) => (
            <List.Item
              style={{
                cursor: 'pointer',
                background: index === selectedIndex ? 'var(--ant-color-primary-bg)' : undefined,
                borderRadius: 4,
                marginBottom: 4,
              }}
              onClick={() => onSelect(m.path)}
            >
              <div
                ref={(r) => { itemRefs.current[index] = r; }}
                style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <Text strong>{m.label}</Text>
                {m.breadcrumb !== m.label && (
                  <Text style={{ fontSize: 12, color: colors.text.secondary }}>{m.breadcrumb}</Text>
                )}
              </div>
            </List.Item>
          )}
        />
      </div>
      {items.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: colors.text.secondary }}>
          No menu items found.
        </div>
      )}
    </Modal>
  );
};

/** Modal listing all global keyboard shortcuts (from constants/shortcuts) */
const KeyboardShortcutsModal: FC<{
  open: boolean;
  onClose: () => void;
  colors: { text: { secondary: string } };
}> = ({ open, onClose, colors }) => (
  <Modal
    title="Keyboard shortcuts"
    open={open}
    onCancel={onClose}
    footer={null}
    width={420}
  >
    <List
      dataSource={GLOBAL_SHORTCUTS}
      renderItem={(s) => (
        <List.Item style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: colors.text.secondary }}>{s.label}</span>
          <kbd style={{ padding: '4px 8px', borderRadius: 4, fontSize: 12, background: 'var(--ant-color-fill-quaternary)' }}>
            {getShortcutDisplay(s.keys)}
          </kbd>
        </List.Item>
      )}
    />
  </Modal>
);

/**
 * Get the menu key for an item: normalized path when path exists, else key as-is.
 * Parents (groups) often have only key; leaves have path.
 */
function getItemKey(item: MenuItem): string {
  return item.path ? normalizePath(item.path) : item.key;
}

/**
 * Flatten menu to all navigable items (with path) for global search.
 * Each entry: { path, label, key, breadcrumb }.
 */
function flattenMenuForSearch(items: MenuItem[], parentLabel = ''): Array<{ path: string; label: string; key: string; breadcrumb: string }> {
  const result: Array<{ path: string; label: string; key: string; breadcrumb: string }> = [];
  for (const item of items) {
    if (item.type === 'divider' || !item.key) continue;
    const breadcrumb = parentLabel ? `${parentLabel} › ${item.label}` : item.label;
    if (item.path) {
      result.push({
        path: normalizePath(item.path),
        label: item.label,
        key: getItemKey(item),
        breadcrumb,
      });
    }
    if (item.children?.length) {
      result.push(...flattenMenuForSearch(item.children, breadcrumb));
    }
  }
  return result;
}
function findParentKeys(items: MenuItem[], targetPath: string): string[] {
  for (const item of items) {
    const key = getItemKey(item);
    if (item.path && normalizePath(item.path) === targetPath) {
      return [];
    }
    if (item.children && item.children.length > 0) {
      if (item.children.some((c) => c.path && normalizePath(c.path) === targetPath)) {
        return [key];
      }
      const deepKeys = findParentKeys(item.children, targetPath);
      if (deepKeys.length > 0) {
        return [key, ...deepKeys];
      }
    }
  }
  return [];
}

/**
 * Transform backend MenuItem[] to Ant Design Menu items.
 * - Parents (group, no path): key=item.key; children rendered as SubMenu. When sidebar collapsed,
 *   Ant Design shows submenus as popups on hover (triggerSubMenuAction="hover"). Clicking parent does not navigate.
 * - Leaves (have path): key=normalizePath(path); onClick navigates to key. Only leaves trigger navigation.
 */
function transformMenuItems(items: MenuItem[]): MenuProps['items'] {
  return items.map((item) => {
    if (item.type === 'divider') {
      return { type: 'divider' as const, key: item.key };
    }

    const children =
      item.children && item.children.length > 0 ? transformMenuItems(item.children) : undefined;

    return {
      key: getItemKey(item),
      icon: getIconComponent(item.icon),
      label: item.label,
      children,
      onTitleClick: children
        ? (e) => {
            const target = e.domEvent.currentTarget as HTMLElement;
            const parentLi = target.closest('li.ant-menu-submenu');
            if (parentLi) {
              setTimeout(() => {
                if (parentLi.classList.contains('ant-menu-submenu-open')) {
                  parentLi.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
              }, 300);
            }
          }
        : undefined,
    };
  });
}

const MainLayout: FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    sidebarCollapsed,
    setSidebarCollapsed,
    sidebarWidth,
    layoutType,
    setLayoutType: _setLayoutType,
    theme,
    customization,
    loadFromAPI: loadGlobalSettings,
  } = useGlobalStore();
  const { loadFromAPI: loadTableSettings } = useTableSettingsStore();
  const COLORS = useColors();
  const currentFontFamily = getFontFamilyFromCustomization(customization.fontFamily, TYPOGRAPHY);
  const [customizationVisible, setCustomizationVisible] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [sidebarDropdownOpen, setSidebarDropdownOpen] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showExpandedText, setShowExpandedText] = useState(false);
  const [menuSearchValue, setMenuSearchValue] = useState('');
  const [menuSearchModalOpen, setMenuSearchModalOpen] = useState(false);
  const [shortcutsModalOpen, setShortcutsModalOpen] = useState(false);
  const menuSearchShortcutDisplay = getShortcutDisplay(SHORTCUT_KEYS.MENU_SEARCH);
  const isInlineMode = layoutType !== 'horizontal';
  const menuInlineCollapsed = isInlineMode && !isMobile && sidebarCollapsed;

  // Transform backend menu to Ant Design format
  const menuItems = useMemo(() => {
    if (!user?.menu || user.menu.length === 0) {
      // Fallback to empty menu if user menu is not loaded
      return [];
    }
    return transformMenuItems(user.menu);
  }, [user?.menu]);

  // Intelligent sidebar highlighting: matches subpaths (e.g. /master/items/123 highlights /master/items)
  const selectedKeys = useMemo(() => {
    const path = location.pathname;
    if (!user?.menu) return [path];

    const flattened = flattenMenuForSearch(user.menu);

    // 1. Try exact match first
    if (flattened.some((i) => i.path === path)) return [path];

    // 2. Try longest prefix match (must match full path segments)
    // We append '/' to both to ensure we don't match partial segments (e.g. /admin/users vs /admin/user-groups)
    const matches = flattened
      .filter((i) => path.startsWith(i.path + '/') || path === i.path)
      .sort((a, b) => b.path.length - a.path.length);

    if (matches.length > 0) return [matches[0].path];

    return [path];
  }, [location.pathname, user?.menu]);

  const [openKeys, setOpenKeys] = useState<string[]>([]);

  // Update openKeys when navigating or menu changes to ensure selected item is visible
  useEffect(() => {
    if (user?.menu && selectedKeys.length > 0 && isInlineMode && !sidebarCollapsed) {
      const parents = findParentKeys(user.menu, selectedKeys[0]);
      if (parents.length > 0) {
        setOpenKeys((prev) => {
          const combined = [...new Set([...prev, ...parents])];
          return combined.length === prev.length ? prev : combined;
        });
      }
    }
  }, [selectedKeys, user?.menu, isInlineMode, sidebarCollapsed]);

  const flattenedMenu = useMemo(() => {
    if (!user?.menu?.length) return [];
    return flattenMenuForSearch(user.menu);
  }, [user?.menu]);

  const filteredMenuForModal = useMemo(() => {
    if (!menuSearchValue.trim()) return flattenedMenu;
    const q = menuSearchValue.trim().toLowerCase();
    return flattenedMenu.filter(
      (m) =>
        m.label.toLowerCase().includes(q) ||
        m.breadcrumb.toLowerCase().includes(q) ||
        m.path.toLowerCase().includes(q),
    );
  }, [menuSearchValue, flattenedMenu]);

  const openMenuSearchModal = useCallback(() => {
    setMenuSearchModalOpen(true);
  }, []);

  const handleMenuSearchSelect = (path: string) => {
    setMenuSearchValue('');
    setMenuSearchModalOpen(false);
    navigate(path);
    if (isMobile) setMobileDrawerOpen(false);
  };

  const collapseButtonRef = useRef<HTMLButtonElement>(null);

  // Responsive breakpoint detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 992); // Ant Design lg breakpoint
      // Auto-collapse sidebar on mobile
      if (window.innerWidth < 992 && !sidebarCollapsed && layoutType !== 'horizontal') {
        setSidebarCollapsed(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [sidebarCollapsed, layoutType, setSidebarCollapsed]);

  // Update collapse button background when sidebar state changes
  useEffect(() => {
    if (collapseButtonRef.current && !isMobile) {
      collapseButtonRef.current.style.backgroundColor = sidebarCollapsed
        ? COLORS.surface.hover
        : 'transparent';
    }
  }, [sidebarCollapsed, isMobile, COLORS.surface.hover]);

  // Defer logo/footer text until after Sider expand (avoids squeeze/jump). Hide immediately on collapse.
  useEffect(() => {
    if (sidebarCollapsed) {
      setShowExpandedText(false);
      return;
    }
    const t = setTimeout(() => setShowExpandedText(true), 200);
    return () => clearTimeout(t);
  }, [sidebarCollapsed]);

  // Load user preferences from API on mount (only when authenticated)
  useEffect(() => {
    if (user) {
      // Load both global settings and table settings from API
      // Falls back to localStorage defaults if API fails
      loadGlobalSettings().catch((error) => {
        console.warn('Failed to load global settings from API:', error);
      });

      loadTableSettings().catch((error) => {
        console.warn('Failed to load table settings from API:', error);
      });
    }
  }, [user, loadGlobalSettings, loadTableSettings]);

  // Global shortcuts (from constants/shortcuts)
  useHotkeys(
    SHORTCUT_KEYS.MENU_SEARCH,
    (e) => {
      e.preventDefault();
      openMenuSearchModal();
    },
    { enableOnFormTags: true },
    [openMenuSearchModal],
  );
  useHotkeys(
    SHORTCUT_KEYS.GO_DASHBOARD,
    (e) => {
      e.preventDefault();
      navigate('/dashboard');
    },
    { enableOnFormTags: true },
    [navigate],
  );
  useHotkeys(
    SHORTCUT_KEYS.PREFERENCES,
    (e) => {
      e.preventDefault();
      setCustomizationVisible(true);
    },
    { enableOnFormTags: true },
    [],
  );

  useHotkeys(
    SHORTCUT_KEYS.SAVE_FORM,
    (e) => {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent(GLOBAL_SAVE_FORM_EVENT));
    },
    { enableOnFormTags: true },
    [],
  );

  useHotkeys(
    'escape',
    () => {
      if (menuSearchModalOpen) setMenuSearchModalOpen(false);
      else if (shortcutsModalOpen) setShortcutsModalOpen(false);
      else if (customizationVisible) setCustomizationVisible(false);
    },
    { enableOnFormTags: true },
    [menuSearchModalOpen, shortcutsModalOpen, customizationVisible],
  );


  const headerStyle = {
    padding: '0 12px', // Horizontal padding only - height controls total size
    height: LAYOUT.header.height,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // Explicitly set background to match sidebar - use elevated surface token
    background: COLORS.surface.elevated,
    // Border to separate header from content - visible in both themes
    borderBottom: `1px solid ${COLORS.border.primary}`,
  };

  // Logo section styling; Sider uses ConfigProvider (siderBg) and its own motion—no style overrides on Sider
  const logoStyle = {
    minHeight: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: sidebarCollapsed ? 0 : SPACING.lg, // 24px when open, 0 when collapsed
    paddingRight: 0,
    marginBottom: sidebarCollapsed ? 0 : SPACING.md, // Add spacing below logo when expanded
  };

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'User';
  const roleLabel = user?.roles?.[0] ?? 'Member';
  const avatarInitials =
    [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join('').toUpperCase() || null;

  const menuContent = (
    <Menu
      theme={theme}
      mode={layoutType === 'horizontal' ? 'horizontal' : 'inline'}
      inlineCollapsed={menuInlineCollapsed}
      selectedKeys={selectedKeys}
      openKeys={sidebarCollapsed ? undefined : openKeys}
      onOpenChange={setOpenKeys}
      triggerSubMenuAction="hover"
      getPopupContainer={() => document.body}
      subMenuCloseDelay={0.2}
      items={menuItems}
      onClick={({ key }) => {
        navigate(key);
        if (isMobile) setMobileDrawerOpen(false);
      }}
      style={{
        border: 'none',
        background: 'transparent',
        fontSize: TYPOGRAPHY.fontSize.sm,
        ...(layoutType === 'horizontal' && {
          lineHeight: LAYOUT.header.height,
          minWidth: 0,
          flex: 1,
        }),
      }}
    />
  );

  // Content: flex 1, minHeight 0, overflow auto so only this area scrolls; Sidebar+Header stay fixed.
  // Ant Design Layout handles Sider collapse/expand motion; Content flexes with it.
  const contentStyle = {
    margin: 0,
    padding: 0,
    flex: 1,
    minHeight: 0,
    overflow: 'auto' as const,
    scrollBehavior: 'smooth' as const,
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'My Profile',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'customization',
      icon: <SettingOutlined />,
      label: 'Preferences',
    },
    {
      key: 'shortcuts',
      icon: <QuestionCircleOutlined />,
      label: 'Keyboard shortcuts',
    },
    {
      key: 'help',
      icon: <QuestionCircleOutlined />,
      label: 'Help & Support',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign Out',
      danger: true,
    },
  ];

  const handleUserMenuClick = ({ key }: { key: string }) => {
    setDropdownVisible(false);
    setSidebarDropdownOpen(false);
    switch (key) {
      case 'profile':
        navigate('/profile');
        if (isMobile) setMobileDrawerOpen(false);
        break;
      case 'customization':
        setCustomizationVisible(true);
        break;
      case 'shortcuts':
        setShortcutsModalOpen(true);
        break;
      case 'help':
        // Handle help navigation
        break;
      case 'logout':
        logout();
        // Clear any cached data and redirect to sign-in
        localStorage.clear();
        window.location.href = '/sign-in';
        break;
    }
  };

  // Horizontal layout: root fixed height so only Content scrolls
  if (layoutType === 'horizontal') {
    return (
      <Layout
        style={{
          height: '100vh',
          minHeight: '100vh',
          overflow: 'hidden',
          fontFamily: currentFontFamily,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Header
          style={{
            ...headerStyle,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            position: 'fixed',
            top: 0,
            width: '100%',
            zIndex: 1000,
            boxShadow: theme === 'dark' ? 'none' : SHADOWS.sm,
          }}
        >
          {/* Logo - Horizontal Layout */}
          <div style={{ padding: `0 ${SPACING.lg}`, minWidth: 200 }}>
            <Space align="center" size="small">
              <div
                style={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '999px',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={APP_LOGO_URL}
                  alt={APP_NAME}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div>
                <Text
                  strong
                  style={{
                    fontSize: TYPOGRAPHY.fontSize.lg,
                    color: COLORS.primary.dark,
                    display: 'block',
                    lineHeight: 1.2,
                    fontWeight: TYPOGRAPHY.fontWeight.semibold,
                  }}
                >
                  {APP_NAME}
                </Text>
                <Text
                  style={{
                    fontSize: TYPOGRAPHY.fontSize.xs,
                    color: COLORS.text.secondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    lineHeight: 1.2,
                  }}
                >
                  {APP_TAGLINE}
                </Text>
              </div>
            </Space>
          </div>

          {/* Global Search + Horizontal Menu */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: SPACING.md }}>
            <Button
              type="text"
              icon={<SearchOutlined />}
              onClick={openMenuSearchModal}
              style={{
                minWidth: 220,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                paddingLeft: 12,
                color: COLORS.text.secondary,
              }}
            >
              <span style={{ marginLeft: 8 }}>Search menu…</span>
              <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.85 }}>({menuSearchShortcutDisplay})</span>
            </Button>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>{menuContent}</div>
          </div>

          {/* Header Actions */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: SPACING.md,
              paddingRight: SPACING.lg,
            }}
          >
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
              trigger={['click']}
              open={dropdownVisible}
              onOpenChange={setDropdownVisible}
              overlayClassName="user-dropdown"
              getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
            >
              <span style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                <Avatar
                  src={user?.imageUrl}
                  icon={!user?.imageUrl && !avatarInitials ? <UserOutlined /> : undefined}
                  style={{
                    backgroundColor: !user?.imageUrl ? COLORS.primary.main : undefined,
                    color: !user?.imageUrl ? COLORS.primary.contrastText : undefined,
                  }}
                >
                  {!user?.imageUrl && avatarInitials ? avatarInitials : null}
                </Avatar>
              </span>
            </Dropdown>
          </div>
        </Header>

        <Content
          style={{
            ...contentStyle,
            marginTop: LAYOUT.header.height,
            flex: 1,
            minHeight: 0,
          }}
        >
          <div
            style={{
              padding: SPACING.lg,
              maxWidth:
                (customization.layoutWidth ?? 'container') === 'container'
                  ? LAYOUT.content.maxWidth
                  : 'none',
              margin: '0 auto',
              width: '100%',
            }}
          >
            <Outlet />
          </div>
        </Content>

        <CustomizationPanel
          open={customizationVisible}
          onClose={() => setCustomizationVisible(false)}
        />
        <MenuSearchModal
          open={menuSearchModalOpen}
          onClose={() => setMenuSearchModalOpen(false)}
          searchValue={menuSearchValue}
          onSearchChange={setMenuSearchValue}
          items={filteredMenuForModal}
          onSelect={handleMenuSearchSelect}
          colors={COLORS}
          menuSearchShortcutDisplay={menuSearchShortcutDisplay}
        />
        <KeyboardShortcutsModal
          open={shortcutsModalOpen}
          onClose={() => setShortcutsModalOpen(false)}
          colors={COLORS}
        />
      </Layout>
    );
  }

  // Vertical layout (default): root fixed height + overflow hidden so only Content scrolls
  return (
    <Layout
      style={{
        height: '100vh',
        minHeight: '100vh',
        overflow: 'hidden',
        fontFamily: currentFontFamily,
        display: 'flex',
      }}
    >
      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          placement="left"
          closable={false}
          onClose={() => setMobileDrawerOpen(false)}
          open={mobileDrawerOpen}
          width={sidebarWidth}
          styles={{
            body: {
              padding: 0,
              background: COLORS.surface.elevated,
            },
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Mobile Drawer Logo - always expanded */}
            <div
              style={{
                minHeight: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                paddingTop: '8px',
                paddingBottom: '8px',
                paddingLeft: SPACING.lg,
                paddingRight: 0,
              }}
            >
              <Space align="center" size="small">
                <div
                  style={{
                    fontSize: '28px',
                    color: COLORS.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {/* Legacy icon left intentionally empty; logo now handled in Sider/header */}
                </div>
                <div>
                  <Text
                    strong
                    style={{
                      fontSize: TYPOGRAPHY.fontSize.lg,
                      color: COLORS.primary.dark,
                      display: 'block',
                      lineHeight: 1.2,
                      fontWeight: TYPOGRAPHY.fontWeight.semibold,
                    }}
                  >
                    SEL TECHNOLOGIES
                  </Text>
                  <Text
                    style={{
                      fontSize: TYPOGRAPHY.fontSize.xs,
                      color: COLORS.text.secondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      lineHeight: 1.2,
                    }}
                  >
                    Enterprise Suite
                  </Text>
                </div>
              </Space>
            </div>
            {/* Global Search */}
            <div style={{ flexShrink: 0, padding: SPACING.sm, borderBottom: `1px solid ${COLORS.border.primary}` }}>
              <Button
                type="text"
                icon={<SearchOutlined />}
                onClick={openMenuSearchModal}
                style={{
                  width: '100%',
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  paddingLeft: 12,
                  color: COLORS.text.secondary,
                }}
              >
                <span style={{ marginLeft: 8 }}>Search menu…</span>
                <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.85 }}>({menuSearchShortcutDisplay})</span>
              </Button>
            </div>
            {/* Menu: scrollable when many items; scrollbar hidden via .sidebar-menu-scroll */}
            <div
              className="sidebar-menu-scroll"
              style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
            >
              {menuContent}
            </div>
            {/* Sidebar Footer: user profile card; only arrow opens same menu as header */}
            <div
              style={{
                flexShrink: 0,
                padding: `${SPACING.md} ${SPACING.lg}`,
                borderTop: `1px solid ${COLORS.border.primary}`,
                boxShadow: SHADOWS.top,
                background: COLORS.surface.container,
                borderRadius: `0 0 ${SPACING.sm}px ${SPACING.sm}px`,
                display: 'flex',
                alignItems: 'center',
                gap: SPACING.md,
              }}
            >
              <Avatar
                src={user?.imageUrl}
                icon={!user?.imageUrl && !avatarInitials ? <UserOutlined /> : undefined}
                size="large"
                style={{
                  backgroundColor: !user?.imageUrl ? COLORS.primary.main : undefined,
                  color: !user?.imageUrl ? COLORS.primary.contrastText : undefined,
                  flexShrink: 0,
                }}
              >
                {!user?.imageUrl && avatarInitials ? avatarInitials : null}
              </Avatar>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text
                  strong
                  style={{
                    fontSize: TYPOGRAPHY.fontSize.xs,
                    color: COLORS.text.primary,
                    display: 'block',
                    lineHeight: 1.3,
                  }}
                >
                  {displayName}
                </Text>
                <Text
                  style={{
                    fontSize: TYPOGRAPHY.fontSize.xs,
                    color: COLORS.text.secondary,
                    display: 'block',
                    lineHeight: 1.3,
                  }}
                >
                  {roleLabel}
                </Text>
              </div>
              <Dropdown
                menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
                trigger={['click']}
                placement="topRight"
                open={sidebarDropdownOpen}
                onOpenChange={setSidebarDropdownOpen}
                overlayClassName="user-dropdown"
                overlayStyle={{ minWidth: 200, width: 200 }}
                getPopupContainer={() => document.body}
              >
                <Button
                  type="text"
                  icon={
                    <RightOutlined
                      style={{
                        transform: sidebarDropdownOpen ? 'rotate(-90deg)' : 'rotate(0deg)',
                        transition: `transform ${ANIMATIONS.duration.normal} ${ANIMATIONS.easing.easeOut}`,
                      }}
                    />
                  }
                  style={{
                    color: COLORS.text.secondary,
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                  aria-label="Open user menu"
                />
              </Dropdown>
            </div>
          </div>
        </Drawer>
      )}

      {/* Desktop Sider - only render when not mobile */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={sidebarCollapsed}
          width={sidebarWidth}
          collapsedWidth={LAYOUT.sidebar.width.collapsed}
          theme={theme}
          style={{
            background: COLORS.surface.elevated,
            borderRight: `1px solid ${COLORS.border.primary}`,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            {/* Logo Section */}
            <div style={logoStyle}>
              {sidebarCollapsed ? (
                <div
                  style={{
                    width: 35,
                    height: 35,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '999px',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={APP_LOGO_URL}
                    alt={APP_NAME}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              ) : (
                <Space align="center" size="small">
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '999px',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={APP_LOGO_URL}
                      alt={APP_NAME}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                  {showExpandedText && (
                    <div>
                      <Text
                        strong
                        style={{
                          fontSize: TYPOGRAPHY.fontSize.lg,
                          color: COLORS.primary.dark,
                          display: 'block',
                          lineHeight: 1.2,
                          fontWeight: TYPOGRAPHY.fontWeight.semibold,
                        }}
                      >
                        {APP_NAME}
                      </Text>
                      <Text
                        style={{
                          fontSize: TYPOGRAPHY.fontSize.xs,
                          color: COLORS.text.secondary,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          lineHeight: 1.2,
                        }}
                      >
                        {APP_TAGLINE}
                      </Text>
                    </div>
                  )}
                </Space>
              )}
            </div>

            {/* Global Search: click opens modal with all menu items */}
            <div
              style={{
                flexShrink: 0,
                padding: SPACING.sm,
                paddingBottom: sidebarCollapsed ? SPACING.sm : SPACING.xs,
                borderBottom: sidebarCollapsed ? 'none' : `1px solid ${COLORS.border.primary}`,
              }}
            >
              <Button
                type="text"
                icon={<SearchOutlined />}
                onClick={openMenuSearchModal}
                style={{
                  width: '100%',
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...(!sidebarCollapsed && {
                    justifyContent: 'flex-start',
                    paddingLeft: SPACING.sm,
                  }),
                }}
              >
                {!sidebarCollapsed && (
                  <>
                    <span style={{ marginLeft: 8, color: COLORS.text.secondary }}>Search menu…</span>
                    <span style={{ marginLeft: 6, fontSize: 11, color: COLORS.text.secondary, opacity: 0.85 }}>({menuSearchShortcutDisplay})</span>
                  </>
                )}
              </Button>
            </div>

            {/* Navigation Menu: flex 1 + overflow so it scrolls; scrollbar hidden via .sidebar-menu-scroll */}
            <div
              className="sidebar-menu-scroll"
              style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
            >
              {menuContent}
            </div>

            {/* Sidebar Footer: user profile card; only arrow opens same menu as header. Collapsed: Avatar is the trigger. */}
            {sidebarCollapsed ? (
              <div
                style={{
                  flexShrink: 0,
                  padding: SPACING.sm,
                  borderTop: `1px solid ${COLORS.border.primary}`,
                  boxShadow: SHADOWS.top,
                  background: COLORS.surface.container,
                  borderRadius: `0 0 ${SPACING.sm}px ${SPACING.sm}px`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Dropdown
                  menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
                  trigger={['click']}
                  placement="topRight"
                  open={sidebarDropdownOpen}
                  onOpenChange={setSidebarDropdownOpen}
                  overlayClassName="user-dropdown"
                  overlayStyle={{ minWidth: 200, width: 200 }}
                  getPopupContainer={() => document.body}
                >
                  <span
                    style={{ cursor: 'pointer', display: 'inline-flex' }}
                    aria-label="Open user menu"
                  >
                    <Avatar
                      src={user?.imageUrl}
                      icon={!user?.imageUrl && !avatarInitials ? <UserOutlined /> : undefined}
                      size="default"
                      style={{
                        backgroundColor: !user?.imageUrl ? COLORS.primary.main : undefined,
                        color: !user?.imageUrl ? COLORS.primary.contrastText : undefined,
                        flexShrink: 0,
                      }}
                    >
                      {!user?.imageUrl && avatarInitials ? avatarInitials : null}
                    </Avatar>
                  </span>
                </Dropdown>
              </div>
            ) : (
              <div
                style={{
                  flexShrink: 0,
                  padding: `${SPACING.md} ${SPACING.lg}`,
                  borderTop: `1px solid ${COLORS.border.primary}`,
                  boxShadow: SHADOWS.top,
                  background: COLORS.surface.container,
                  borderRadius: `0 0 ${SPACING.sm}px ${SPACING.sm}px`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACING.md,
                }}
              >
                <Avatar
                  src={user?.imageUrl}
                  icon={!user?.imageUrl && !avatarInitials ? <UserOutlined /> : undefined}
                  size="large"
                  style={{
                    backgroundColor: !user?.imageUrl ? COLORS.primary.main : undefined,
                    color: !user?.imageUrl ? COLORS.primary.contrastText : undefined,
                    flexShrink: 0,
                  }}
                >
                  {!user?.imageUrl && avatarInitials ? avatarInitials : null}
                </Avatar>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {showExpandedText && (
                    <>
                      <Text
                        strong
                        style={{
                          fontSize: TYPOGRAPHY.fontSize.xs,
                          color: COLORS.text.primary,
                          display: 'block',
                          lineHeight: 1.3,
                        }}
                      >
                        {displayName}
                      </Text>
                      <Text
                        style={{
                          fontSize: TYPOGRAPHY.fontSize.xs,
                          color: COLORS.text.secondary,
                          display: 'block',
                          lineHeight: 1.3,
                        }}
                      >
                        {roleLabel}
                      </Text>
                    </>
                  )}
                </div>
                <Dropdown
                  menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
                  trigger={['click']}
                  placement="topRight"
                  open={sidebarDropdownOpen}
                  onOpenChange={setSidebarDropdownOpen}
                  overlayClassName="user-dropdown"
                  overlayStyle={{ minWidth: 200, width: 200 }}
                  getPopupContainer={() => document.body}
                >
                  <Button
                    type="text"
                    icon={
                      <RightOutlined
                        style={{
                          transform: sidebarDropdownOpen ? 'rotate(-90deg)' : 'rotate(0deg)',
                          transition: `transform ${ANIMATIONS.duration.normal} ${ANIMATIONS.easing.easeOut}`,
                        }}
                      />
                    }
                    style={{
                      color: COLORS.text.secondary,
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                    aria-label="Open user menu"
                  />
                </Dropdown>
              </div>
            )}
          </div>
        </Sider>
      )}

      <Layout
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header - fixed in column, no scroll */}
        <Header style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.md }}>
            <Button
              ref={collapseButtonRef}
              type="text"
              icon={
                isMobile ? (
                  <MenuUnfoldOutlined />
                ) : sidebarCollapsed ? (
                  <MenuUnfoldOutlined />
                ) : (
                  <MenuFoldOutlined />
                )
              }
              onClick={() => {
                if (isMobile) {
                  setMobileDrawerOpen(true);
                } else {
                  setSidebarCollapsed(!sidebarCollapsed);
                }
              }}
              style={{
                fontSize: TYPOGRAPHY.fontSize.base,
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor:
                  !isMobile && sidebarCollapsed ? COLORS.surface.hover : 'transparent',
              }}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  if (sidebarCollapsed) {
                    // When collapsed: hover shows lighter background
                    e.currentTarget.style.backgroundColor =
                      theme === 'dark' ? COLORS.surface.container : COLORS.primary.lighter;
                  } else {
                    // When expanded: hover shows hover background
                    e.currentTarget.style.backgroundColor = COLORS.surface.hover;
                  }
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  if (sidebarCollapsed) {
                    // When collapsed: return to active background
                    e.currentTarget.style.backgroundColor = COLORS.surface.hover;
                  } else {
                    // When expanded: return to transparent
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.md }}>
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
              trigger={['click']}
              overlayClassName="user-dropdown"
              open={dropdownVisible}
              onOpenChange={setDropdownVisible}
              overlayStyle={{ minWidth: 200, width: 200 }}
              getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
            >
              <span style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                <Avatar
                  src={user?.imageUrl}
                  icon={!user?.imageUrl && !avatarInitials ? <UserOutlined /> : undefined}
                  style={{
                    backgroundColor: !user?.imageUrl ? COLORS.primary.main : undefined,
                    color: !user?.imageUrl ? COLORS.primary.contrastText : undefined,
                  }}
                >
                  {!user?.imageUrl && avatarInitials ? avatarInitials : null}
                </Avatar>
              </span>
            </Dropdown>
          </div>
        </Header>

        {/* Main Content - only this area scrolls; Sidebar + Header stay fixed */}
        <Content style={contentStyle}>
          <div
            style={{
              padding: SPACING.lg,
              maxWidth:
                (customization.layoutWidth ?? 'container') === 'container'
                  ? LAYOUT.content.maxWidth
                  : 'none',
              margin: '0 auto',
              width: '100%',
            }}
          >
            <Outlet />
          </div>
        </Content>
      </Layout>

      <CustomizationPanel
        open={customizationVisible}
        onClose={() => setCustomizationVisible(false)}
      />
      <MenuSearchModal
        open={menuSearchModalOpen}
        onClose={() => setMenuSearchModalOpen(false)}
        searchValue={menuSearchValue}
        onSearchChange={setMenuSearchValue}
        items={filteredMenuForModal}
        onSelect={handleMenuSearchSelect}
        colors={COLORS}
        menuSearchShortcutDisplay={menuSearchShortcutDisplay}
      />
      <KeyboardShortcutsModal
        open={shortcutsModalOpen}
        onClose={() => setShortcutsModalOpen(false)}
        colors={COLORS}
      />
    </Layout>
  );
};

export default MainLayout;
